import React, { useEffect, useState, useRef } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Path, Circle } from 'react-native-svg';
import * as shape from 'd3-shape';
import styled from 'styled-components/native';
import { StrongText, MetricStackTitle, ShinyBlockOuter, ShinyBody, ShinyHeading, PageWrapper, BasicText, Loading } from '../Styles';
import { TabularColumn } from '../core/Tabular';
import _ from 'lodash';
import { ColorBlock } from '../Things';
import BarChart from '../core/BarChart';
import { COLORS } from '../../WasteTheme';
import Metrics, { Metric } from '../core/Metrics';
import * as scale from 'd3-scale';
import moment from 'moment';
import { NewTabular } from './SustainabilityDashboard';
import Util from '../core/Util';

const styles = StyleSheet.create({
    containerStyle: {
        flexDirection: "column",
        flexBasis: "49%",
        justifyContent: "space-evenly",
        borderBottomLeftRadius: 5,
        borderBottomRightRadius: 5,
        marginTop: 5,
        marginRight: 5,
        marginBottom: 10,
        marginLeft: 5
    }
});

const CustomMetricsTiles = ({data = {}}) => {
    const [metrics, setMetrics] = useState([]);

    //const groupOrder = ["Equivalent emissions of passenger cars driven in one year", "Equivalent emissions of homes in one year", "Equivalent carbon sequestered by trees grown in one year"];

    useEffect(()=>{
        setMetrics(_.map(_.keys(data), (groupName)=>{
            return <View style={{flexDirection: "row", justifyContent: "space-around", flexWrap: "wrap"}} key={groupName}>
                { _.map(data[groupName], (metric)=>{
                    return <Metric 
                        key={groupName+metric.periodLength}
                        supertitle={metric.periodTo ?? (metric.periodLength + " Months")}
                        superPosition="left"
                        subtitle={metric.subtitle} 
                        value={Util.roundNice(metric.metricValue, 0)} 
                        icon={metric.icon}
                        colour={metric.colour}
                        style={{flexBasis: Util.isMobile()? "95%" : "40%", marginVertical: 10, justifyContent: "space-between"}}
                    />
                }) }
            </View>
        }));
    }, [data]);

    //mobile: justify-content: center;
    const MetricsSection = styled.View`
        z-index: 9999;
        flex-direction: column;
    `;

    return <MetricsSection>
        {metrics}
    </MetricsSection>
}

const Equivalencies = ({portalData}) => {
    const [data, setData] = useState({});

    useEffect(()=>{
        setData( _.groupBy(_.orderBy(_.filter(portalData.metrics["sustainability"], (m)=>["environmentalImpact"].indexOf(m.category) > -1), "periodLength", "desc"), "subtitle") );
    }, [portalData]);

    return <ShinyBlockOuter minHeight={400} flexBasis={Util.isMobile()? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>GHG Equivalent</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <CustomMetricsTiles data={data}/>
        </ShinyBody>
    </ShinyBlockOuter>;
}

const formatChartDate = (d, customFormat, inputFormat) => {
    let format = "MMM";
    if(customFormat != undefined && customFormat.trim().length > 0){
        format = customFormat;
    }
    
    if(Util.isMobile()){
        return moment(d, inputFormat).format(format);
    }
    return moment(d, inputFormat).format(format);
}

const GreenhouseGraph = ({portalData, monthlyMetrics = {}, ghgTarget = null}) => {
    const [data, setData] = useState([]);

    useEffect(()=>{
        const dates = {};
        _.forEach(portalData.dateRange || [], (date)=>{
            dates[moment(date).format("YYYY-MM")] = null;
        });

        let newData = [];

        _.forEach( monthlyMetrics, (data, month)=>{
            let sum = _.sumBy(data, "metricValue");

            dates[moment(month, "MMMM YYYY").format("YYYY-MM")] = "seen";
            
            newData.push({
                label: formatChartDate(month, null, "MMMM YYYY"), 
                value: sum, 
                legendLabel: month, 
                units: "MT CO₂", 
                svg: {fill: COLORS.wsc_blue}
            });
        });
        
        const unseenDates = _.filter(_.map(dates, (v,k)=>{
            if(v==null){
                return k
            }
            return null
        }), (el)=>el!=null);

        _.forEach(unseenDates, (date)=>{
            newData.push({
                label: formatChartDate(date, null, "YYYY-MM"), 
                value: 0, 
                legendLabel: formatChartDate(date, "MMMM YYYY", "YYYY-MM"), 
                units: "MT CO₂", 
                svg: {fill: COLORS.wsc_blue}
            });
        });

        newData = newData.sort((a,b)=>{
            return moment(a.legendLabel, "MMM YYYY").diff(moment(b.legendLabel, "MMM YYYY"))
        });
        
        setData(newData);
    },[monthlyMetrics]);

    const xAxisFormatLabel = (value, index) => {
        return value;
    }

    const yAxisFormatLabel = (value, index) => {
        let precision = 0;
        if(Math.abs(value) < 10){
            precision = 2;
        } else if(Math.abs(value) < 100){
            precision = 1;
        }  
        return " " + Util.roundNice(value, precision);
    }

    let graphHeight = 400;
    if(Util.isMobile()){
        graphHeight = 300;
    }

    const legendChildren = (data, hoveredBand) => {
        if(hoveredBand == null){
            return null;
        }
        
        return <View style={{flexDirection: "row"}}>
            <StrongText style={{marginRight: 10}}>{hoveredBand.legendLabel}</StrongText>
            <StrongText>{Util.roundNice(hoveredBand.value, 2) + " MT CO₂"}</StrongText>
            {ghgTarget != null? 
            <View style={{flexDirection: "row", marginLeft: 20, alignItems: "center"}}>
                <View style={{height: 5, flexBasis: 20, flexGrow: 0, backgroundColor: COLORS.navy, marginRight: 5}}/>
                <StrongText>Target</StrongText>
            </View> : null}
        </View>;
    }

    const yearRange = [];
    let yearLabel = "";
    if(portalData.dateRange != undefined && portalData.dateRange.length > 0){
        yearRange.push(moment(portalData.dateRange[0]).format("YYYY"));

        const endRange = moment(portalData.dateRange[portalData.dateRange.length-1]).format("YYYY");
        if(endRange != yearRange[0]){
            yearRange.push(endRange);
        }

        yearLabel = _.uniq(yearRange).join(" - ");
    }

    return <ShinyBlockOuter minHeight={graphHeight} flexBasis={Util.isMobile()? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>Net Greenhouse Gas</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <BarChart 
                data={data}
                defaultDataPoint={data != undefined? _.last(_.filter(data, (d)=>d.value > 0)) : undefined}
                legendChildren={(data, hoveredBand)=>legendChildren(data, hoveredBand)}
                yAccessor={ ({item}) => { return item.value; } }
                xAccessor={ ({item}) => { return item.label; } }
                xAxisFormatLabel={xAxisFormatLabel} 
                yAxisFormatLabel={yAxisFormatLabel}
                height={graphHeight}
                showGrid={true}
                showLabels={false}
                showLegend={true}
                yAxisLabel="MT CO₂e"
                xAxisLabel={yearLabel}
                extraKids={<LineChartOnTop target={ghgTarget}/>}
            />
        </ShinyBody>
    </ShinyBlockOuter>;
}

const LineChartOnTop = (props) => {
    const { x, y, data, yAccessor, xAccessor, bandwidth, target } = props;

    if(target == null){
        return;
    }

    const mappedData = data.map((item, index) => ({
        y: target,
        x: index
    }))

    const line = shape
        .line()
        .x((d,idx) =>{
            if(idx == 0){
                return x(d.x);
            }

            if(idx == mappedData.length-1){
                return x(d.x) + bandwidth;
            }

            return x(d.x) + bandwidth / 2
        })
        .y((d) => y(d.y))
        .defined((item) => typeof item.y === 'number')
        .curve(shape.curveLinear)(mappedData);

    const svg = { stroke: COLORS.navy, strokeWidth: 5 };

    return <Path
        fill={'none'}
        {...svg}
        d={line}
        animate={false}
        animationDuration={0}
    />;
}

const Contributors = ({portalData, metricData = {}, title = "GHG Key Contributors"}) => {
    const [data, setData] = useState([]);
    const maxValue = useRef({value: undefined});

    useEffect(()=>{
        const newData = _.map(metricData, (materialGroup, materialName)=>{
            let sum = _.sumBy(materialGroup, "metricValue");

            const currentDataPoint = {
                label: materialName,
                value: sum,
                svg: {fill: materialGroup[0].colour}
            }

            if(maxValue.current.value == undefined || Math.abs(currentDataPoint.value) > Math.abs(maxValue.current.value)){
                maxValue.current = currentDataPoint;
            }

            return currentDataPoint;
        });

        setData(newData);
    },[metricData]);

    let graphHeight = 400;
    if(Util.isMobile()){
        graphHeight = 300;
    }

    const xAxisFormatLabel = (value, index) => {
        let precision = 0;
        if(Math.abs(value) < 10){
            precision = 2;
        } else if(Math.abs(value) < 100){
            precision = 1;
        }

        if(index % 2 == 0){
            return Util.roundNice(value, precision);
        }

        return "";
    }

    const legendChildren = (data, hoveredBand) => {
        if(hoveredBand == null){
            return null;
        }
        
        return <View style={{flexDirection: "row"}}>
            <View style={{height: 20, flexBasis: 20, flexGrow: 0, backgroundColor: hoveredBand.svg.fill}}/> 
            <View style={{flexBasis: 10}}/> 
            <View >
                <StrongText>{hoveredBand.label}</StrongText> 
            </View>
            <View style={{flexBasis: 10}}/>
            <View >
                <StrongText>{Util.roundNice(hoveredBand.value, 2) + " MT CO₂"}</StrongText>
            </View>
        </View>;
    }
    
    return <ShinyBlockOuter minHeight={graphHeight} flexBasis={Util.isMobile()? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>{title} ({portalData.dateRange.length || 0} Months)</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <BarChart
                data={data}
                height={graphHeight}
                horizontal={true}
                showGrid={true}
                showLegend={true}
                legendChildren={(data, hoveredBand)=>legendChildren(data, hoveredBand)}
                xAccessor={ ({item}) => { return item.label; } }
                xAxisAccessor={ ({item}) => { return item; }}
                xAxisScale={scale.scaleLinear}
                xAxisFormatLabel={xAxisFormatLabel}
                yAccessor={ ({item}) => { return item.value; } }
                yAxisAccessor={ ({item}) => { return item.label; }}
                yAxisScale={scale.scaleBand}
                defaultDataPoint={maxValue.current.value == undefined? undefined : maxValue.current}
                xAxisLabel="MT CO₂e"
            />
        </ShinyBody>
    </ShinyBlockOuter>;
}


const Composition12Month = ({dateRange = [], metricData = {}}) => {
    const [details, setDetails] = useState({cols: [], data: []});

    useEffect(()=>{
        if( !_.isEmpty(metricData) ){
            let monthlyCols = [];
            const materialColumn = <TabularColumn 
                key="material" 
                data={(row) =>{
                    return <View style={{flexDirection: "row", alignItems: "center"}}>
                        <ColorBlock color={row.colour} style={{marginHorizontal: 5}}/>
                        <BasicText>{row.descriptor}</BasicText>
                    </View>
                }}
                title="Material (MT CO₂e)" 
                className="material-col"
            />;

            monthlyCols.push(materialColumn);

            const monthlyWaste = _.map( metricData, (materialValues, materialName)=>{
                const row = _.pick( materialValues[0], "descriptor", "colour");
                
                _.each( materialValues, (g)=>{
                    if(row[g.periodFrom] == undefined){
                        row[ g.periodFrom ] = 0;
                    }
                    
                    row[ g.periodFrom ] += g.metricValue;
                });
                return row;
            });

            _.each( dateRange, (m)=>{
                const month = moment(m);
                const niceMonth = month.format('MMM YY');
                const className = niceMonth == moment().format('MMM YY') ? "current-month" : "";
                let highlight = false;

                if(niceMonth == moment().format('MMM YY')){
                    highlight = true;
                }

                const columnStyle = {};

                if(highlight == true){
                    columnStyle.backgroundColor = "rgba(203, 203, 203, 0.5)";
                }

                monthlyCols.push( <TabularColumn key={"month-" + month} data={ (row)=>Util.roundNice(row[month.format("MMMM YYYY")], 2) } title={niceMonth} className={className} style={columnStyle} /> );
            });
    
            setDetails({cols: monthlyCols, data: monthlyWaste});
        }
    }, [metricData, dateRange]);

    return <ShinyBlockOuter flexBasis={"95%"}>
        <ShinyHeading>
            <MetricStackTitle>GHG Contributors</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <ScrollView horizontal={true} contentContainerStyle={{flexDirection: "row", flexGrow: 1}}>
                <NewTabular data={details.data}>
                    {details.cols}
                </NewTabular>
            </ScrollView>
        </ShinyBody>
    </ShinyBlockOuter>;
}

const CarbonImpact = ({portalData}) =>{
    if (portalData.materials == null){
        return <PageWrapper></PageWrapper>;
    }
    
    let data = {};
    let epaMetrics = {};
    let monthlyMetrics = {};
    if(portalData.metrics != undefined){
        data = _.groupBy(_.orderBy(_.filter(portalData.metrics["sustainability"], (m)=>["ghgPrevented", "ghgProduced", "ghgNet"].indexOf(m.category) > -1), "periodLength", "desc"), "category");
        epaMetrics = _.groupBy(_.filter(portalData.metrics["sustainability"], {category: "epa"}), "descriptor");
        monthlyMetrics = _.groupBy(_.filter(portalData.metrics["sustainability"], {category: "epa"}), "periodFrom");
    }

    let targetGHG = 0;
    if(portalData.clients != undefined){
        const divisor = portalData.clients.length;

        if(divisor > 0){
            targetGHG = parseInt(Util.roundNice( _.sum(_.map(portalData.clients, "target_ghg"))/divisor , 0));
        }
    }

    return <PageWrapper>
        <Metrics data={data} categoryOrder={["ghgProduced", "ghgPrevented", "ghgNet"]}/>
        <Equivalencies portalData={portalData} />
        <Contributors metricData={epaMetrics} portalData={portalData} />
        <GreenhouseGraph monthlyMetrics={monthlyMetrics} portalData={portalData} ghgTarget={targetGHG}/>
        <Composition12Month dateRange={portalData.dateRange} metricData={epaMetrics} />
    </PageWrapper>
};

export default CarbonImpact;